import {
  cellOutline,
  composeFrame,
  createRenderer,
  createRendererSource,
  createRendererSourceFromUrl,
  destroyRendererSource,
  destroyUnused,
  exceedsTextureLimit,
  fileKeyOf,
  findShared,
  isStillUsed,
  isVideoSource,
  naturalSizeOf,
  sourceKindOf,
  summarizePlayOutcomes,
  tryPlay,
  waitForSourceReady,
  type DisplayTransform,
  type Renderer,
  type RendererSource,
  type SourceKind,
} from '@signa/renderer';
import {
  cellKey,
  correctionsFor,
  isCurved,
  linkedCells,
  outlinePoints,
  slotCountOf,
  toNormalized,
  type CellRef,
  type FrameLike,
  type NormalizedPoint,
  type PublicTemplate,
} from '@signa/shared';
import {
  BACKGROUND_RETRY_DELAYS,
  fetchTemplate,
  loadImage,
  VIEWER_ERROR_CODES,
  ViewerError,
  type ViewerErrorCode,
} from './api.js';
import {
  ASSIGN_ERROR_CODES,
  assignCodeOf,
  blobKindOf,
  identityKeyOf,
  isUnsupportedBlob,
  pickDropCell,
  placementFailure,
  readyFrames,
  resolveAssignTarget,
  resolveClearTargets,
  resolveSourceKind,
  SignaPreviewError,
  sourceUrlOf,
  URL_LOAD_TIMEOUT_MS,
  urlKeyOf,
  type AssignSource,
  type AssignTarget,
  type PlaceFailure,
  type ReadyFrame,
} from './assign.js';
import { exportVideo, primeVideos, warnExportFailure } from './encode.js';
import { errorMessage, isRetryable, messages, type Lang } from './messages.js';
import { askInOrder, NoticeDialog } from './notice-dialog.js';
import {
  autoStartedPlayback,
  controlsState,
  exportControlsState,
  joinPositionOf,
  needsPartialConfirm,
  playableOf,
  showsNoUploadNotice,
  startedPlayback,
  stoppedPlayback,
  STOPPED_PLAYBACK,
  type PlaybackState,
} from './playback.js';

import { viewerStyles, viewportHeightLimits } from './styles.js';
import { uiParts, type UiMode, type UiParts } from './ui-mode.js';
import { sendView } from './view-beacon.js';

/**
 * ビューアシェル。
 *
 * **V01(embed.js)と V02(単体ページ)の共有部分**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md))。
 * 差分は「入口(トークンの取得方法)と外枠」だけなので、ここに寄せる。
 *
 * 責務:
 * - テンプレートの取得と状態表示(ローディング / エラー / 再試行)
 * - 素材(動画・画像)の D&D とセルへの割り当て(**当てる手段は D&D だけ** —— #159)
 * - 描画コアの生死管理
 *
 * **素材の割り当て状態はここが保持する。** コンテキストロストで Renderer の
 * 内部状態が失われても、ここから `setSource` を再実行すれば復帰できる(#33)。
 */

/**
 * シェルの設定。**盤面のトークンで取るか、取得済みのテンプレートを渡すか**のどちらか(#267)。
 */
export type ShellOptions = TokenShellOptions | TemplateShellOptions;

/** 盤面のトークンで公開 API から取る(V01 / V02 の盤面)。 */
interface TokenShellOptions extends BaseShellOptions {
  readonly token: string;
  readonly template?: undefined;
}

/**
 * **取得済みのテンプレートを渡す**(共有フォルダのタブ —— #267)。
 *
 * - **公開 API を叩かない**(共有フォルダの応答に全タブの中身が入っている —— ADR-0046)
 * - 背景画像の待ちでは**取り直さず、画像だけを再試行する**(盤面ごとのトークンが無い)
 * - **閲覧の通知を送らない**(盤面ごとのトークンが無い —— 共有フォルダのページが `onEvent` の
 *   `ready` を受けて、タブの `id` で送る。#290 / ADR-0051)
 */
interface TemplateShellOptions extends BaseShellOptions {
  readonly template: PublicTemplate;
  readonly token?: undefined;
}

interface BaseShellOptions {
  readonly apiBase: string;
  readonly fit?: 'contain' | 'cover';
  readonly maxWidth?: number | null;
  /**
   * **ビューポートの高さに収める**(#212)。**V02(プレビュー URL)だけが渡す。**
   *
   * 既定(V01 = 埋め込み)では**幅にしか追従しない** —— 高さは
   * [`applyAspectRatio`](#applyAspectRatio) が縦横比から導く。埋め込み先の
   * ページには他の要素があり、**こちらが画面の高さを占有してはいけない。**
   *
   * V02 は**盤面だけを見せる画面**なので事情が逆で、**画面に収まっていないと
   * スクロールしないと全体が見えない** —— 16:9 の画面で 16:9 の盤面を開くと、
   * **ブラウザ UI の分だけ必ず縦に溢れていた。**
   */
  readonly fitViewport?: boolean;
  readonly autoplay?: boolean;
  /**
   * **書き出しの操作を出すか**(#181)。
   *
   * **既定は出す。** V02(社内の単体ページ)は常に有効で、
   * **V01 は `data-signa-export` の値をここへ渡す**(既定は出さない —— 同意書 Q3)。
   */
  readonly exportEnabled?: boolean;
  /**
   * **表示の型**(#283)。**V01 だけが `data-signa-ui` の値を渡す**(V02 は既定)。
   * 何を出すかは [`uiParts`](./ui-mode.ts) が決める。
   */
  readonly ui?: UiMode;
  readonly lang?: Lang;
  /**
   * 状態変化の通知。V01 は `CustomEvent` に変換する。V02 の盤面の単体ページは使わない。
   * **共有フォルダのページは `ready` を受けて閲覧を通知する**(#290)。
   */
  readonly onEvent?: (event: ShellEvent) => void;
  /**
   * **動画の書き出しを始めた / 終えた**(#267)。共有フォルダのページが、書き出しの間
   * タブを止めるのに使う(書き出しの途中でシェルを捨てさせない)。
   *
   * **`onEvent` には流さない** —— 流すと埋め込み先に `CustomEvent` が増え、
   * **埋め込みの公開の口が変わる。**
   */
  readonly onBusyChange?: (busy: boolean) => void;
  /**
   * **テンプレートを取れた**(#318 / [ADR-0053](../../../../docs/adr/0053-share-link-logo.md) の決定 4)。
   * **V02 の盤面のページだけが渡す** —— `showLogo` を見て、盤面の外にロゴの帯を出す。
   *
   * 描画の前に呼ぶ(帯の分を先に差し引けば、盤面が一度縮むことが無い)。**再試行のたびに呼ぶ**
   * ので、受け取る側は 2 回目以降を無視する。取得に失敗したとき(期限切れ・失効を含む)は呼ばない。
   *
   * **`onEvent` には流さない** —— 流すと埋め込みの公開の口(`CustomEvent`)が変わる
   * (`onBusyChange` と同じ理由)。
   */
  readonly onTemplate?: (template: PublicTemplate) => void;
}

export type ShellEvent =
  | {
      readonly type: 'ready';
      readonly frameCount: number;
      /** 枠の一覧(#284)。**埋め込み先が枠と盤面の照合に使う。** */
      readonly frames: readonly ReadyFrame[];
    }
  | { readonly type: 'error'; readonly code: ViewerErrorCode }
  | {
      readonly type: 'videoassigned';
      readonly frameId: string;
      /** 枠の中のセル番号(#152)。**分割していない枠は `0`。** */
      readonly cellIndex: number;
      readonly frameCount: number;
      /**
       * 盤面のセルの総数(#152)。**分割していない枠は 1 つ数える。**
       *
       * **`assignedCount` と単位をそろえるために足した。** #152 で
       * 割り当てがセル単位になり、`assignedCount` が `frameCount` を
       * 超えうるようになった —— 「全部埋まったか」を
       * `assignedCount === frameCount` で判定していると壊れる。
       */
      readonly cellCount: number;
      /** 割り当て済みの**セル**の数(#152 で枠からセルへ)。 */
      readonly assignedCount: number;
    }
  | { readonly type: 'play'; readonly frameId: string }
  | { readonly type: 'pause'; readonly frameId: string };

interface Assignment extends CellRef {
  /** 動画または画像(#158)。**同じファイルなら複数のセルで共有する**(#168)。 */
  readonly source: RendererSource;
  /**
   * 出どころのファイルの鍵(#168)。
   *
   * **同じファイルを 2 度ドロップしたときに要素を使い回す**ために持つ ——
   * ドロップごとに `<video>` を作ると**復号器がセルの数だけ立つ。**
   */
  readonly fileKey: string;
}

/** [`place`](#place) に渡す素材の用意(#284)。**入口(ドロップ / `assign`)ごとに決める。** */
interface PreparedSource {
  readonly kind: SourceKind;
  /** 共有の鍵。ドロップは `fileKeyOf`、`assign` は同一性か URL(ADR-0048 の決定 3)。 */
  readonly key: string;
  /** 要素を作る。**共有できる要素があれば呼ばない。** */
  readonly create: () => RendererSource;
  /** 書き出しの最中なら入れない(`assign` だけ —— ドロップの挙動は変えない)。 */
  readonly rejectWhileExporting: boolean;
  /**
   * 読み込みを待つ上限(ミリ秒)。**URL の素材だけ**に付ける —— 通信が止まると
   * `loadedmetadata` も `error` も来ず、`assign` がいつまでも終わらない(#284 の最終レビュー)。
   */
  readonly loadTimeoutMs?: number;
}

type PlaceResult =
  | { readonly ok: true; readonly cells: readonly CellRef[]; readonly source: RendererSource }
  | { readonly ok: false; readonly reason: PlaceFailure };

/**
 * 盤面のセルの総数(#152)。**分割していない枠も 1 つ数える。**
 *
 * 公開イベントの `assignedCount` と単位をそろえるために出す ——
 * 埋め込み先が「全部埋まったか」を数で判定できるようにする。
 *
 * **入れ物の数で数える**(#339)。折れた枠は素材が全体で 1 本なので 1 つ —— 分割の数で数えると、
 * セル 0 を埋めても**永久に「全部埋まった」にならない。**
 */
function totalCells(frames: readonly FrameLike[]): number {
  return frames.reduce((sum, frame) => sum + slotCountOf(frame), 0);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export class ViewerShell {
  private readonly lang: Lang;
  /** 表示の型から決まる「出すもの・受けるもの」(#283)。 */
  private readonly parts: UiParts;
  /**
   * シェルが差し込んだスタイル。**捨てるときに一緒に外す**(#267)。
   *
   * 共有フォルダはタブを切り替えるたびにシェルを作り直すので、残すと切り替えの数だけ溜まる。
   */
  private readonly style: HTMLStyleElement;
  private readonly root: HTMLDivElement;
  private readonly frame: HTMLDivElement;
  private readonly stageHost: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private readonly ui: HTMLDivElement;
  private readonly dropzone: HTMLDivElement;
  /** ドロップ先の枠を縁取る SVG(#124)。 */
  private readonly dropOutline: SVGSVGElement;
  private readonly message: HTMLDivElement;
  private readonly videoHost: HTMLDivElement;

  private renderer: Renderer | null = null;
  private template: PublicTemplate | null = null;
  private transform: DisplayTransform = { width: 0, height: 0, offsetX: 0, offsetY: 0 };
  private readonly assignments = new Map<string, Assignment>();

  /**
   * 再生・停止の見張りを張り終えた素材(#168)。
   *
   * **共有すると同じ要素に何度も張ってしまう。** `WeakSet` なので
   * 素材を破棄すれば自然に落ちる。
   */
  private readonly watched = new WeakSet<RendererSource>();
  private destroyed = false;
  /**
   * **描画まで終わったか**(#284)。`assign` / `clear` はこれが立つまで `NOT_READY`。
   * 読み込みをやり直すと(再試行)下ろす。
   */
  private ready = false;
  /** 読み込みに失敗したときのコード。`assign` はこれで reject する(`LINK_EXPIRED` など)。 */
  private failure: ViewerErrorCode | null = null;
  /** WebGL2 が使えない(フォールバック)。素材を載せられないので `assign` は `UNSUPPORTED`。 */
  private unsupported = false;
  /** セル → 最後の要求の番号(**後勝ち** —— ADR-0048 の決定 2)。 */
  private readonly latestRequest = new Map<string, number>();
  /** 読み込み中の要素(共有の鍵 → 要素)。**まとめて呼んだ同じ素材も要素を 1 つにする。** */
  private readonly loading = new Map<string, RendererSource>();
  /** 読み込みを待っている要求の数(要素 → 数)。**待たれている要素は捨てない。** */
  private readonly holders = new Map<RendererSource, number>();
  /** 捨てた要素。**共有元が待っている間に消えたか**の判定に使う。 */
  private readonly retired = new WeakSet<RendererSource>();
  private requestSeq = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  // --- 再生の操作(#159)---------------------------------------------------
  private readonly notice: HTMLDivElement;
  private readonly actions: HTMLDivElement;
  private readonly controls: HTMLDivElement;
  private readonly playButton: HTMLButtonElement;
  private readonly pauseButton: HTMLButtonElement;
  private readonly syncButton: HTMLButtonElement;
  /**
   * 静止画(PNG)の保存(#180 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md))。
   *
   * **画像だけが入っているときに出す**(同意書 Q2)。動画があるときは
   * 動画の書き出し(#181)が出るので、こちらは引っ込む。
   */
  private readonly saveImageButton: HTMLButtonElement;
  /** 動画の書き出し(#181)。**動画が 1 本でもあれば出す。** */
  private readonly exportButton: HTMLButtonElement;
  /** 書き出しの中止(#181)。**書き出し中だけ出す。** */
  private readonly cancelExportButton: HTMLButtonElement;
  /** 書き出し・保存の前の利用上の注意(#259)。**毎回出す。** */
  private readonly noticeDialog: NoticeDialog;
  /**
   * 書き出し中のオーバーレイ(#233)。
   *
   * **ビューアの枠を覆い、中止以外の操作を受け付けない。**
   * 書き出し中は表示の描画を止める([`setPaused`](../../../renderer/src/types.ts))ので
   * **盤面が空白になりうる** —— 覆っておけば、それが自然に見える。
   */
  private readonly exportOverlay: HTMLDivElement;
  /** 進捗の文字(「書き出し中… 45%」)。 */
  private readonly exportLabel: HTMLDivElement;
  /** 進捗バーの塗り。**幅で進捗を表す。** */
  private readonly exportFill: HTMLDivElement;
  /**
   * 進行中の書き出し(#181)。`null` なら走っていない。
   *
   * **二重に始めさせない。** 走っている間はボタンを引っ込め、中止だけを出す。
   */
  private exporting: AbortController | null = null;
  /**
   * 再生の状態(#159)。**`playing` と時間軸を一緒に動かす。**
   *
   * 別々に持つと「再生中なのに時間軸が止まっている」という食い違いができ、
   * **次の「再生」で全部が頭へ巻き戻る。** 遷移は
   * [`playback.ts`](./playback.ts) の純関数に置いてある。
   *
   * **`<video>` どうしを直接比べない。** 長さが違うとループを跨いだ瞬間に
   * 巻き戻り、同じ位置なのに大きくずれて見える(#123)。
   */
  private playback: PlaybackState = STOPPED_PLAYBACK;
  /**
   * 画面に収める上限から差し引く高さ(px —— #267)。**`fitViewport` のときだけ効く。**
   * 共有フォルダのページが、見出しとタブの帯の高さを [`setViewportOffset`](#setViewportOffset) で渡す。
   */
  private viewportOffset = 0;

  constructor(
    container: ParentNode & { append: (...nodes: Node[]) => void },
    private readonly options: ShellOptions,
  ) {
    this.lang = options.lang ?? 'ja';
    this.parts = uiParts(options.ui ?? 'default');
    const m = messages(this.lang);

    this.style = document.createElement('style');
    this.style.textContent = viewerStyles();

    this.root = el('div', 'signa-root');
    if (typeof options.maxWidth === 'number') {
      this.root.style.maxWidth = `${String(options.maxWidth)}px`;
    }

    this.frame = el('div', 'signa-frame');
    this.stageHost = el('div', 'signa-stage-host');
    // 高さは `aspect-ratio` で決める(`applyAspectRatio`)。**`height: 100%`
    // にはしない** —— 親の高さが中身依存なので解決できず 0 になる。
    this.stageHost.setAttribute(
      'style',
      'display: flex; align-items: center; justify-content: center; width: 100%;',
    );
    if (options.fitViewport === true) this.limitToViewport();

    this.status = el('div', 'signa-status');
    this.status.textContent = m.loading;

    /*
     * **`ui` そのものは隠さない**(#159 の退行の原因)。
     *
     * `ui` は D&D の受け口を含む。読み込み中に隠すつもりで `hidden` にすると、
     * **戻し忘れたときにドロップがページへ素通りし、ブラウザがファイルを
     * 別タブで開く。** 隠すのは中身(受け口と操作の帯)だけにする。
     */
    this.ui = el('div', 'signa-ui');

    this.dropzone = el('div', 'signa-dropzone');
    this.dropzone.dataset.active = 'false';
    const dropLabel = el('div', 'signa-dropzone-label');
    dropLabel.textContent = m.dropHint;
    this.dropzone.append(dropLabel);

    /*
     * **ドロップ先のセルだけを縁取る**(#124 → #152)。
     *
     * 以前は `signa-dropzone` が画面全体(`inset: 0`)を縁取っていたが、
     * **どの枠に入るのかが分からなかった。** 落とす前に入る先を見せる。
     * 分割した盤面では枠でも足りないので、**セルの輪郭**を出す。
     *
     * **`pickCell` の規則は変えない**(#33)—— 枠の外に落としても
     * 空きセルに入る。その行き先がここに出るので、
     * **フォールバックが目に見えるようになる。**
     */
    this.dropOutline = document.createElementNS(SVG_NS, 'svg');
    this.dropOutline.setAttribute('class', 'signa-drop-outline');
    this.dropOutline.setAttribute('aria-hidden', 'true');
    this.dropzone.append(this.dropOutline);

    this.notice = el('div', 'signa-notice');
    this.notice.textContent = m.noUpload;

    this.message = el('div', 'signa-message');
    this.message.hidden = true;

    /*
     * 再生 / 停止 / 同期(#159)。
     *
     * **素材が当たるまで出さない。** 押しても何も起きないボタンは
     * 「壊れている」と読める。**静止画だけのときも出さない**(#158)——
     * `<img>` に `currentTime` は無い。
     *
     * **`autoplay: false` でも出す。** 自動で始まらないだけで、
     * 閲覧者が自分で流せるべきである。
     */
    this.controls = el('div', 'signa-controls');
    this.playButton = this.makeControl(m.play, () => {
      void this.playAll(false);
    });
    this.pauseButton = this.makeControl(m.pause, () => {
      this.pauseAll();
    });
    this.syncButton = this.makeControl(m.sync, () => {
      void this.playAll(true);
    });
    /*
     * **静止画の保存**(#180 / ADR-0036)。
     *
     * **画像だけのときに出す**(同意書 Q2)—— 動く要素が無いので動画にする意味は薄いが、
     * 「今の見え方を 1 枚で渡したい」は成立する。
     */
    this.saveImageButton = this.makeControl(m.saveImage, () => {
      void this.saveImage();
    });
    this.controls.append(this.playButton, this.pauseButton, this.syncButton);

    /*
     * **動画の書き出し**(#181 / ADR-0036)。
     *
     * **静止画保存と同じく `controls` の外に置く** —— こちらは動画があるときに出るので
     * `controls` の中でも消えはしないが、**出す条件が違うものを同じ入れ物に入れない。**
     */
    this.exportButton = this.makeControl(m.exportVideo, () => {
      void this.runExport();
    });
    this.cancelExportButton = this.makeControl(m.exportCancel, () => {
      this.exporting?.abort();
    });

    this.actions = el('div', 'signa-actions');
    /*
     * **静止画保存は `controls` の外に置く。**
     *
     * `controls`(再生 / 停止 / 同期)は**動画が 1 本も無いと丸ごと隠れる** ——
     * 静止画保存はまさにその状況で出したいので、中に入れると一緒に消える。
     */
    this.actions.append(this.controls, this.saveImageButton, this.exportButton, this.notice);

    /*
     * **書き出し中のオーバーレイ**(#233)。
     *
     * **中止ボタンはこの中に置く。** 操作帯に置いたままだと、
     * **覆った層の下になって押せなくなる。**
     */
    this.exportOverlay = el('div', 'signa-export-overlay');
    this.exportOverlay.hidden = true;
    this.exportLabel = el('div', 'signa-export-label');
    const exportBar = el('div', 'signa-export-bar');
    this.exportFill = el('div', 'signa-export-fill');
    exportBar.append(this.exportFill);
    /*
     * **タブを表示したままにしてもらう**(#354)。隠れたタブでは書き出しが進まない。
     *
     * **2 つの案内は 1 つの要素に改行で並べる。** 要素を分けるとオーバーレイの間隔(`gap`)が
     * もう 1 つ増え、**狭い埋め込みで溢れやすくなる。**
     */
    const exportNote = el('div', 'signa-export-note');
    exportNote.textContent = `${m.exportBusy}\n${m.exportKeepVisible}`;
    this.exportOverlay.append(this.exportLabel, exportBar, this.cancelExportButton, exportNote);
    this.ui.append(this.dropzone, this.message, this.actions);

    this.videoHost = el('div', 'signa-videos');

    // **オーバーレイは最後に置く。** 盤面と操作の両方を覆う(#233)。
    this.frame.append(this.stageHost, this.status, this.ui, this.exportOverlay);
    // 利用上の注意(#259)。`showModal` で最上位に出るので、置き場所は外枠でよい。
    this.noticeDialog = new NoticeDialog({ body: m.exportNotice, cancel: m.noticeCancel });
    this.root.append(this.frame, this.videoHost, this.noticeDialog.element);
    container.append(this.style, this.root);

    /*
     * **最初の状態をそろえる**(#159)。
     *
     * 受け口はテンプレートを読むまで出さない(枠があるか分からない)。
     * 素材は 0 件なので、操作は出さず注意は出す。
     */
    this.showDropSurface(false);
    this.updateControls();

    this.bindDragAndDrop();
  }

  /**
   * 取得と描画を開始する。
   *
   * **取得済みのテンプレートを渡されていれば取得しない**(共有フォルダのタブ —— #267)。
   * 再試行のボタンもここを呼ぶので、そのときは同じテンプレートで組み直す。
   */
  async start(): Promise<void> {
    // **再試行でも呼ばれる。** 前の失敗を消し、描画が終わるまで `assign` を受けない(#284)。
    this.ready = false;
    this.failure = null;
    this.showLoading();
    if (this.options.template !== undefined) {
      this.template = this.options.template;
    } else {
      try {
        this.template = await fetchTemplate(this.options.apiBase, this.options.token);
      } catch (error) {
        this.fail(error instanceof ViewerError ? error.code : VIEWER_ERROR_CODES.NETWORK);
        return;
      }
      if (this.destroyed) return;
    }

    this.options.onTemplate?.(this.template);
    await this.mountRenderer(this.template);
  }

  /**
   * **画面に収める上限から、ページの見出しなどの高さを差し引く**(#267)。
   *
   * `fitViewport` のときだけ効く(埋め込みは画面の高さを占有しないので、上限そのものが無い)。
   * 共有フォルダのページが、見出しとタブの帯の高さが変わるたびに呼ぶ。
   */
  setViewportOffset(px: number): void {
    this.viewportOffset = px;
    if (this.options.fitViewport === true) this.limitToViewport();
  }

  /**
   * ステージの高さをキャンバス比から決める。
   *
   * **幅は埋め込み先が決め、高さは比で決まる**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md)
   * の「`canvas.width` / `canvas.height` はアスペクト比の算出に使う。
   * 表示サイズの決定には使わない(埋め込み先の幅に追従するため)」)。
   *
   * **高さを中身依存にしてはいけない。** 描画コアは親を測ってステージの
   * 寸法を決めるため(packages/renderer/src/webgl-renderer.ts の `resize`)、
   * 「親の高さ ← 中身 ← 親の測定値」で循環し**永久に 0 のまま**になる
   * (#84 の通し確認で踏んだ)。
   */
  /**
   * **ステージの高さをビューポートに収める**(#212)。**V02 だけで呼ぶ。**
   *
   * ## なぜ `aspect-ratio` だけでは足りなかったか
   *
   * [`applyAspectRatio`](#applyAspectRatio) は `width: 100%` と縦横比から
   * **高さを導く。** 幅は満たせるが、**ビューポートの高さは計算に入らない** ——
   * 16:9 の画面(1920×1080)でブラウザ UI が 100px を使うと、
   * ビューポートは 1920×980 なのに高さは `1920 × 9 / 16 = 1080` になり、
   * **必ず 100px 溢れていた。**
   *
   * ## 縦横比は崩れない
   *
   * 上限で `stageHost` の縦横比は崩れるが、**その中のステージは描画コアが
   * `contain` で収める**([`computeTransform`](../../../renderer/src/layout.ts) は
   * 幅と高さの両方を見る)。`stageHost` は中央寄せなので、余った分は左右に出る。
   *
   * ## なぜ 2 回代入するのか
   *
   * **`dvh` は `vh` より新しい。** 非対応のブラウザは 2 回目の代入を無視するので、
   * **1 回目の `vh` が残る。**
   *
   * **`vh` だけでは足りない。** モバイルの `vh` は**アドレスバーを含まない
   * 最大の高さ**で、バーが出ている間は画面より大きい —— 報告された
   * 「タブなどではみ出す」がまさにこれ。`dvh` は**いま見えている高さ**に追従する。
   *
   * **`aspect-ratio` は外さない。** 高さを中身依存にすると
   * 「親の高さ ← 中身 ← 親の測定値」で循環し、**永久に 0 のまま**になる(#84)。
   */
  private limitToViewport(): void {
    // **順に代入する。** 非対応の値は捨てられ、前のものが残る
    // (順序の意味は `VIEWPORT_HEIGHT_LIMITS` に書いた)。
    // 見出しなどの高さを差し引くとき(#267)も順序は同じ(`viewportHeightLimits`)。
    for (const value of viewportHeightLimits(this.viewportOffset)) {
      this.stageHost.style.maxHeight = value;
    }
  }

  private applyAspectRatio(canvas: { readonly width: number; readonly height: number }): void {
    if (canvas.width <= 0 || canvas.height <= 0) return;
    this.stageHost.style.aspectRatio = `${String(canvas.width)} / ${String(canvas.height)}`;
  }

  private async mountRenderer(template: PublicTemplate): Promise<void> {
    // **描画コアを載せる前に高さを決める。** 0 のまま mount すると
    // ステージが 0×0 で作られる。
    this.applyAspectRatio(template.canvas);

    // 背景があるなら取得できることを確かめる。**変換中は 404 が返りうる。**
    if (template.background !== null) {
      const ok = await this.waitForBackground(template.background.url);
      if (this.destroyed) return;
      if (!ok) {
        // 上限を超えたら手動再試行へ切り替える(#33)。
        this.fail(VIEWER_ERROR_CODES.NETWORK);
        return;
      }
    }

    /*
     * **空のセルに案内を出す**(#208)。
     *
     * ビューアは**取引先のサイトで初見の訪問者が触る画面**で、素材を入れるまで
     * 背景写真が 1 枚出ているだけだった —— **D&D が発見できないと
     * 機能に到達できない**(割り当ては #124 / #159 で D&D だけに絞った)。
     *
     * **S04 は渡さない。** 編集ハンドルとレイヤーパネルがあるので要らない。
     */
    const hints = messages(this.lang);
    this.renderer = createRenderer({
      fit: this.options.fit ?? 'contain',
      /*
       * **`minimal` では輪郭を描かない**(#283)。WebGL 側は案内を渡さなければ描かず、
       * フォールバック(WebGL2 非対応)は `fallbackOutlines: false` で止める。
       */
      fallbackOutlines: this.parts.fallbackOutlines,
      /*
       * **`minimal` では案内を渡さない**(#283)—— 空のセルの輪郭と案内
       * (タッチ端末の「PC で試せます」を含む)が出なくなる。埋め込み先が素材を API で入れる構成では、
       * **利用者にできない操作の案内**になる。
       */
      ...(this.parts.emptyCellHints
        ? {
            emptyCellHint: {
              /*
               * **種別ごとの文言を一式渡す。** どれを出すかは描画コアが枠の
               * `sourceKind` で選ぶ(#240 / ADR-0038)—— ここで枠ごとに写して回らない。
               */
              drop: {
                any: hints.emptyCellDrop,
                video: hints.emptyCellDropVideo,
                image: hints.emptyCellDropImage,
              },
              touch: hints.emptyCellTouch,
            },
          }
        : {}),
    });
    this.renderer.mount(
      this.stageHost,
      {
        canvas: template.canvas,
        backgroundUrl: template.background?.url ?? null,
        videoFrames: template.videoFrames,
        obstacles: template.obstacles,
      },
      {
        onError: (e) => {
          // **UNSUPPORTED は落とさない。** 背景(既定の表示なら輪郭も)は出ているので、
          // 案内だけ添えて操作は続けさせる。
          this.emit({ type: 'error', code: e.code as ViewerErrorCode });
          /*
           * **素材を載せられない**ので、`assign` は `UNSUPPORTED` で reject する(#284 / #283 の C)。
           * **表示の型に関係なく立てる** —— `minimal` でも `assign` は reject する必要がある。
           */
          if (e.code === VIEWER_ERROR_CODES.UNSUPPORTED) this.unsupported = true;
          // **`minimal` では文言を出さない**(#283)—— 埋め込み先が `signa:error` を受けて案内する。
          if (e.code === VIEWER_ERROR_CODES.UNSUPPORTED && this.parts.unsupportedMessage) {
            this.showMessage(errorMessage(VIEWER_ERROR_CODES.UNSUPPORTED, this.lang));
          }
        },
        onContextLost: () => {
          // **止めるのは動画だけ**(#158)。
          for (const { source } of this.assignments.values()) {
            if (isVideoSource(source)) source.pause();
          }
          // **位置は保つ** —— 復帰したら続きから流し直せる。
          this.playback = stoppedPlayback(this.playback, Date.now());
          this.updateControls();
        },
        onContextRestored: () => {
          // **割り当てを再実行する。** テクスチャは失われている(#33)。
          for (const { frameId, cellIndex, source } of this.assignments.values()) {
            this.renderer?.setSource(frameId, cellIndex, source);
            if (this.options.autoplay !== false) void tryPlay(source);
          }
          /*
           * **操作 UI を再生中に戻す**(#159)。戻さないと、動画は流れているのに
           * 「停止」が押せず**利用者が止められない。**
           */
          if (this.options.autoplay !== false && this.videos().length > 0) {
            this.playback = autoStartedPlayback(this.playback, Date.now());
          }
          this.updateControls();
        },
        onTransform: (transform) => {
          this.transform = transform;
        },
      },
    );

    this.hideLoading();
    /*
     * **枠が 0 個なら D&D の受け口を出さない**(落とす先がない — #33)。
     *
     * **`ui` ごと隠さない。** `signa-message` が `ui` の中にあるため、
     * 隠すと「この環境では合成表示に対応していません」まで消える ——
     * そして**受け口も消えてドロップがページへ素通りする。**
     */
    this.showDropSurface(template.videoFrames.length > 0);
    this.ready = true;
    this.emit({
      type: 'ready',
      frameCount: template.videoFrames.length,
      frames: readyFrames(template.videoFrames),
    });

    /*
     * **表示が終わってから閲覧を通知する**(#272 / [ADR-0042](../../../../docs/adr/0042-view-count-beacon.md))。
     * 取得や背景の待ちで失敗した経路(`fail`)はここに来ないので数えない。
     * **待たない** —— 通知の成否は表示に関係しない。
     *
     * **取得済みのテンプレート(共有フォルダのタブ —— #267)では送らない。** 盤面ごとの
     * トークンが無いので、**共有フォルダのページが `ready` を受けてタブの `id` で送る**
     * (#290 / [ADR-0051](../../../../docs/adr/0051-folder-view-count.md))。
     */
    if (this.options.template === undefined) {
      sendView(this.options.apiBase, this.options.token);
    }
  }

  /**
   * 背景画像を取得できるまで待つ。
   *
   * **指数バックオフで最大 5 回・上限およそ 30 秒**(#33)。
   * 公開 API の再取得も伴わせる —— TTL 60 秒のキャッシュが古い可能性がある。
   *
   * **取得済みのテンプレート(共有フォルダのタブ —— #267)では取り直さず、画像だけを
   * 再試行する。** 盤面ごとのトークンが無く、フォルダを取り直すとタブの並びまで変わりうる。
   */
  private async waitForBackground(initialUrl: string): Promise<boolean> {
    let url = initialUrl;
    if (await loadImage(url)) return true;

    for (const delay of BACKGROUND_RETRY_DELAYS) {
      this.emit({ type: 'error', code: VIEWER_ERROR_CODES.BACKGROUND_PENDING });
      await this.sleep(delay);
      if (this.destroyed) return false;

      if (this.options.template === undefined) {
        try {
          const fresh = await fetchTemplate(this.options.apiBase, this.options.token);
          this.template = fresh;
          // 変換が完了して URL が変わることも、null に戻ることもある。
          if (fresh.background === null) return true;
          url = fresh.background.url;
        } catch {
          // 取得できなくても画像だけ再試行する価値はある。
        }
      }

      if (await loadImage(url)) return true;
    }
    return false;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.retryTimer = setTimeout(resolve, ms);
    });
  }

  // -------------------------------------------------------------------------
  // 動画の受け渡し
  // -------------------------------------------------------------------------

  /**
   * D&D の受け口と操作の帯を出すか。
   *
   * **`signa-ui` は隠さない。** あれには受け口が入っており、隠したまま
   * 戻し忘れると**ドロップがページへ素通りしてブラウザがファイルを開く**
   * (#159 で実際に起きた)。隠すのは中身だけにする。
   */
  private showDropSurface(visible: boolean): void {
    // **`minimal` では受け口を出さない**(#283)。操作の帯(再生 / 停止 / 同期)は出す。
    this.dropzone.hidden = !visible || !this.parts.dropTarget;
    this.actions.hidden = !visible;
  }

  /**
   * D&D を受ける。
   *
   * **受け口はビューア全体(`root`)。** 以前は受け口の矩形だけに付けており、
   * **1px でも外すとブラウザがファイルを開いてしまった**(ドロップの既定動作は
   * 「そのファイルへ移動」)。狙って落とす画面ではないので、
   * **ビューアの上に落ちたものはすべて受ける。**
   *
   * どのセルに入るかは `pickCell` が決める —— 枠の外に落としても
   * 空きセルへ回す(#33 の「とりあえず落とした操作を失敗にしない」)。
   */
  private bindDragAndDrop(): void {
    // **`dragover` で preventDefault しないとドロップが発火しない。**
    // しないままだと `drop` が来ず、ブラウザがファイルへ移動する。
    this.root.addEventListener('dragover', (event) => {
      event.preventDefault();
      /*
       * **`minimal` ではドロップで入れない**(#283)。**`preventDefault` は続ける** —— やめると
       * ブラウザがファイルを開き、埋め込み先のページから離れる。カーソルは「置けない」にする。
       */
      if (!this.parts.acceptsDrop) {
        if (event.dataTransfer !== null) event.dataTransfer.dropEffect = 'none';
        return;
      }
      if (event.dataTransfer !== null) event.dataTransfer.dropEffect = 'copy';
      this.showDropTarget(this.dropPoint(event));
    });

    this.root.addEventListener('dragleave', (event) => {
      // **子要素をまたぐたびに消さない。** ビューアの外へ出たときだけ。
      const to = event.relatedTarget;
      if (to instanceof Node && this.root.contains(to)) return;
      this.hideDropTarget();
    });

    this.root.addEventListener('drop', (event) => {
      event.preventDefault();
      // **`minimal` では受けない**(#283)。既定の動作(ファイルを開く)だけは止めてある。
      if (!this.parts.acceptsDrop) return;
      this.hideDropTarget();
      const file = event.dataTransfer?.files[0];
      if (file === undefined) return;
      void this.acceptFile(file, this.dropPoint(event));
    });
  }

  /**
   * ドロップ先になる**セル**を縁取る(#124 → #152)。
   *
   * **落とす前に入る先を見せる。** 以前は画面全体を縁取っていて、
   * どの枠に入るのか分からなかった。分割した盤面では枠全体でも足りない ——
   * **どのセルに入るか**まで見せる。
   *
   * **`acceptFile` と同じ `pickCell` を通す** —— 表示と実際の行き先が
   * 食い違わないようにする。
   */
  private showDropTarget(position: { x: number; y: number } | null): void {
    const frames: readonly FrameLike[] = this.template?.videoFrames ?? [];
    const normalized =
      position === null
        ? null
        : toNormalized(position, { width: this.transform.width, height: this.transform.height });
    const target = this.pick(normalized, frames);

    if (target === null || this.transform.width <= 0) {
      this.hideDropTarget();
      return;
    }

    /*
     * **連動先もまとめて光らせる**(#254 / #241)。
     *
     * **落とすと配られる先が、手を離す前に分かる**ようにする ——
     * 連動番号が付いていると他の枠にも入るが、それまでは**落としてからでないと分からなかった。**
     *
     * **配り先と同じ計算を使う**([`linkedCells`](../../../shared/src/grid.ts))。
     * ここで別の判定を書くと、**光る先と実際に入る先がずれる。**
     * 連動していなければ自分だけが返るので、**分岐は要らない。**
     */
    const cells = linkedCells(frames, target);

    const { width, height, offsetX, offsetY } = this.transform;
    this.dropOutline.setAttribute('width', String(width));
    this.dropOutline.setAttribute('height', String(height));
    this.dropOutline.style.left = `${String(offsetX)}px`;
    this.dropOutline.style.top = `${String(offsetY)}px`;

    /*
     * **セルごとに 1 つ作り直す。** 数はドラッグ中に変わる(枠をまたぐ・連動の有無)ので、
     * 使い回すより**毎回入れ替える**ほうが消し漏れが起きない。
     */
    this.dropOutline.replaceChildren();
    for (const cell of cells) {
      const frame = frames.find((f) => f.id === cell.frameId);
      if (frame === undefined) continue;
      // セルの輪郭が出せなければ枠全体に落とす(潰れたクアッドなど)。
      // 折れた枠はセル 0 の輪郭が枠全体(#339)。`linkedCells` が寄せているので番号は 0 だけが来る。
      // **曲面の枠は見えている部分だけ**(#361)。裏に回ったセル(連動先)は `null` —— 枠全体に落とさず、光らせない。
      const outline =
        cellOutline(frame.quad, frame.grid, cell.cellIndex, frame.folds, frame.curve) ??
        (isCurved(frame) ? null : frame.quad);
      if (outline === null) continue;
      const shape = document.createElementNS(SVG_NS, 'polygon');
      // **見た目は落とす先と同じ**(#254 の同意書 Q1)。クラスを分けない。
      shape.setAttribute('class', 'signa-drop-outline-shape');
      shape.setAttribute('points', outlinePoints(outline, { width, height }));
      this.dropOutline.append(shape);
    }

    // **1 つも描けなかったら出さない**(枠が消えた直後など)。
    if (this.dropOutline.childElementCount === 0) {
      this.hideDropTarget();
      return;
    }
    this.dropzone.dataset.active = 'true';
  }

  /**
   * ドロップ先のセルを決める。**表示と実際の割り当てで同じものを使う。**
   *
   * 判定は [`pickDropCell`](./assign.ts)(DOM を使わずに試せるよう外に出した —— 曲面の枠を含む)。
   */
  private pick(point: NormalizedPoint | null, frames: readonly FrameLike[]): CellRef | null {
    return pickDropCell(point, frames, new Set(this.assignments.keys()));
  }

  private hideDropTarget(): void {
    this.dropzone.dataset.active = 'false';
  }

  /** ドロップ位置をステージのローカル座標にする。 */
  private dropPoint(event: DragEvent): { x: number; y: number } | null {
    const rect = this.dropzone.getBoundingClientRect();
    if (this.transform.width <= 0) return null;
    // ステージは中央寄せなので、余白分を引く。
    const offsetX = (rect.width - this.transform.width) / 2;
    const offsetY = (rect.height - this.transform.height) / 2;
    return {
      x: event.clientX - rect.left - offsetX,
      y: event.clientY - rect.top - offsetY,
    };
  }

  /**
   * 再生・停止を埋め込み先へ通知する見張りを張る(#168 で作り直した)。
   *
   * ## なぜ素材ごとに 1 度だけか
   *
   * 以前はドロップのたびに `target.frameId` を閉じ込んだリスナを張っていた。
   * **共有すると同じ要素にセルの数だけ積み**、しかも**外す経路が無い** ——
   * その素材を持たなくなったセルの `play` / `pause` が飛び続ける。
   *
   * **発火時に「いまその素材を載せているセル」を引く。** こうすると、
   * 共有していないときの挙動(1 セル = 1 通知)がそのまま保たれる。
   */
  private watchPlayback(source: RendererSource): void {
    if (this.watched.has(source)) return;
    this.watched.add(source);
    const notify = (type: 'play' | 'pause'): void => {
      for (const assignment of this.assignments.values()) {
        if (assignment.source === source) this.emit({ type, frameId: assignment.frameId });
      }
    };
    source.addEventListener('play', () => {
      notify('play');
    });
    source.addEventListener('pause', () => {
      notify('pause');
    });
  }

  /**
   * ドロップを受ける。**入れる先と素材を決めて、共有の経路([`place`](#place))に渡す。**
   *
   * **失敗はビューアの中に文言で出し、`signa:error` を飛ばす**(利用者の操作だから)。
   * `assign`(#284)は同じ経路を通り、失敗を reject で返す —— 違うのはここだけ。
   */
  private async acceptFile(file: File, position: { x: number; y: number } | null): Promise<void> {
    const m = messages(this.lang);
    const kind = sourceKindOf(file);
    if (kind === null) {
      this.showMessage(m.notSupported);
      return;
    }

    const frames: readonly FrameLike[] = this.template?.videoFrames ?? [];
    const normalized =
      position === null
        ? null
        : toNormalized(position, { width: this.transform.width, height: this.transform.height });
    const target = this.pick(normalized, frames);
    if (target === null) return;

    /*
     * **同じファイルなら要素を使い回す**(#168 /
     * [ADR-0031](../../../../../docs/adr/0031-source-keyed-upload.md) の決定 4)。
     *
     * ドロップごとに `<video>` を作ると、同じ動画を 4 セルに入れただけで
     * **復号器が 4 本**立つ。同時に使えるハードウェア復号器には上限があり、
     * 超えるとソフトウェア復号に落ちて CPU が張り付く ——
     * 「一部の動画だけ進みが遅い」がこの形で起こる。
     *
     * **鍵は `fileKeyOf`(中身の属性)。** 同じファイルを 2 度ドロップすると別の `File` になる
     * ので、同一性では共有できない(`assign` は同一性 —— ADR-0048 の決定 3)。
     */
    const placed = await this.place(target, {
      kind,
      key: fileKeyOf(file),
      create: () => createRendererSource(file, kind),
      rejectWhileExporting: false,
    });

    if (placed.ok) {
      await this.afterPlace(placed.cells, placed.source);
      return;
    }
    switch (placed.reason) {
      case 'load-failed':
        // **種類ごとに理由を分ける。** 「動画形式が再生できない」と
        // 画像に言われても直しようがない。
        this.showMessage(kind === 'video' ? m.videoUnsupported : m.imageUnsupported);
        this.emit({ type: 'error', code: VIEWER_ERROR_CODES.VIDEO_UNSUPPORTED });
        return;
      case 'too-large': {
        /*
         * **大きすぎる素材は貼れない**(#158)。GPU のテクスチャの上限を超えると
         * `texImage2D` が失敗し、**セルが空のまま**になる ——
         * 「入れたのに出ない」が最も分かりにくいので、理由を出して弾く。
         *
         * **ドロップでは `VIDEO_UNSUPPORTED` のまま**(既存の埋め込み先でイベントの意味を変えない ——
         * #284 の同意書 Q4。`assign` は `SOURCE_TOO_LARGE`)。
         */
        const limit = this.renderer?.maxSourceSize() ?? 0;
        this.showMessage(m.tooLargeLimit.replace('{n}', String(limit)));
        this.emit({ type: 'error', code: VIEWER_ERROR_CODES.VIDEO_UNSUPPORTED });
        return;
      }
      case 'shared-gone':
        this.showMessage(m.retryAssign);
        return;
      case 'destroyed':
      case 'superseded':
      case 'busy':
        // **黙って終える。** 破棄された・後の操作が勝った・書き出し中(ドロップでは起きない)。
        return;
    }
  }

  /**
   * **セルに素材を入れる共有の経路**(ドロップと `assign` —— #284 / ADR-0048 の決定 7)。
   *
   * 連動先への配布・同じ素材の共有・差し替えと破棄はここに 1 本化する。**失敗の伝え方は呼び出し側**
   * (ドロップは文言とイベント、`assign` は reject)。
   *
   * ## 待ちが明けたら状態を確かめ直す
   *
   * 素材の読み込みは非同期で、その間に**同じセルへの次の要求**(`assign` / `clear` / ドロップ)・
   * **破棄**・**共有元の破棄**が起きうる。**セルごとに最後の要求の番号**を持ち、
   * 自分が最後でなければ割り当てない(**後勝ち** —— ADR-0048 の決定 2)。判定の順は
   * [`placementFailure`](./assign.ts) に閉じてある。
   *
   * ## 読み込み中の要素も共有する(#284 の最終レビュー)
   *
   * 割り当ての表に入るのは読み込みが明けた後なので、表だけを見ると**まとめて呼んだ同じ素材**
   * (`Promise.all` で各キーへ)が別々の要素になり、**復号器がキーの数だけ立つ。**
   * 読み込み中の要素を鍵ごとに覚え(`loading`)、そこも共有の候補にする。
   * **待たれている要素は捨てない**(`holders`)—— 捨てる判断は「表にも無く、誰も待っていない」とき。
   *
   * @param ticket 呼び出し側が先に番号を振ったとき(`assign` の作り直し)。**作り直しで番号を振り直すと、
   *   その間に来た `clear` / `assign` に負けていたことが消える。**
   */
  private async place(
    target: CellRef,
    prepared: PreparedSource,
    ticket?: number,
  ): Promise<PlaceResult> {
    const frames: readonly FrameLike[] = this.template?.videoFrames ?? [];
    /*
     * **連動先すべてに配る**(#241 / [ADR-0039](../../../../docs/adr/0039-frame-link-assignment.md))。
     *
     * **連動していなければ自分だけが返る**ので、ここで分岐しない ——
     * 「返ってきたセルすべてに入れる」と書けば、番号のない枠でも同じ経路を通る。
     */
    const cells = linkedCells(frames, target);
    const myTicket = ticket ?? this.stamp(cells);

    const existing =
      findShared(this.assignments.values(), prepared.key) ?? this.loading.get(prepared.key) ?? null;
    const source = existing ?? prepared.create();
    // **使い回すときは差し込まない。** 既に `videoHost` の中にある。
    if (existing === null) {
      this.videoHost.append(source);
      this.loading.set(prepared.key, source);
    }

    this.hold(source);
    let loaded: boolean;
    try {
      loaded = await this.waitForLoad(source, prepared.loadTimeoutMs);
    } finally {
      this.release(source, prepared.key);
    }

    const size = loaded ? naturalSizeOf(source) : { width: 0, height: 0 };
    const failure = placementFailure({
      destroyed: this.destroyed,
      latest: this.isLatest(cells, myTicket),
      retired: this.retired.has(source),
      loaded,
      empty: loaded && (size.width === 0 || size.height === 0),
      tooLarge: loaded && exceedsTextureLimit(size, this.renderer?.maxSourceSize() ?? 0),
      busy: prepared.rejectWhileExporting && this.exporting !== null,
    });
    if (failure !== null) {
      /*
       * **使わない要素は捨てる。** ただし表で使われている・ほかの要求が待っている要素は残す
       * ([`retireIfUnused`](#retireIfUnused))—— 共有している要素を捨てると**他のセルの絵が消える。**
       */
      this.retireIfUnused(source);
      return { ok: false, reason: failure };
    }

    // **同じセルに 2 回入れたら差し替える**(#33)。連動先も同じ扱い。
    const replaced: { readonly source: RendererSource }[] = [];
    for (const cell of cells) {
      const previous = this.assignments.get(cellKey(cell));
      if (previous !== undefined) replaced.push(previous);
      this.assignments.set(cellKey(cell), { ...cell, source, fileKey: prepared.key });
    }

    /*
     * **前の要素は「誰も使っていなければ」破棄する**(#168)。
     *
     * 共有しているのに捨てると**他のセルの絵が消える。** 判定は表から
     * 導出する —— 参照カウンタは表と食い違っても気づけない。
     *
     * **表を進めきってから 1 回だけ見る**(ADR-0039 決定 4)。
     * `set` の前に見ると、差し替え先が同じ要素だった場合に「まだ使っている」を
     * 取りこぼす。**途中で見ると、進んでいない表を基準にして破棄を取りこぼす。**
     */
    if (replaced.length > 0) {
      destroyUnused(replaced, this.assignments.values(), (gone) => {
        this.retireIfUnused(gone);
      });
    }
    for (const cell of cells) {
      this.renderer?.setSource(cell.frameId, cell.cellIndex, source);
    }
    return { ok: true, cells, source };
  }

  /**
   * 入れ終えた後の共通の処理: 操作の帯、`signa:videoassigned`、再生の時間軸への合流と自動再生。
   */
  private async afterPlace(cells: readonly CellRef[], source: RendererSource): Promise<void> {
    const m = messages(this.lang);
    const frames: readonly FrameLike[] = this.template?.videoFrames ?? [];
    this.showMessage(null);
    // **操作ボタンと注意表示をそろえる**(#159)。
    this.updateControls();

    /*
     * **連動先の分も知らせる**(#241)。
     *
     * 埋め込み先は「どのセルに入ったか」でこちらの状態を追う —— 連動で入った分を
     * 黙っていると、**表示している数と実際の割り当てがずれる。**
     */
    for (const cell of cells) {
      this.emit({
        type: 'videoassigned',
        frameId: cell.frameId,
        cellIndex: cell.cellIndex,
        frameCount: frames.length,
        cellCount: totalCells(frames),
        assignedCount: this.assignments.size,
      });
    }

    // **再生の通知は動画にだけ。** 静止画には再生の概念がない(#158)。
    if (!isVideoSource(source)) return;

    /*
     * **走っている時間軸に合わせてから再生する**(#123)。
     * 合わせないと「当てた順にずれる」—— まさに報告された症状になる。
     */
    const join = joinPositionOf(this.playback, Date.now());
    if (join !== null) {
      const to = correctionsFor([source], join)[0]?.to;
      if (to !== undefined) source.currentTime = to;
    }

    this.watchPlayback(source);

    if (this.options.autoplay === false) return;
    const outcome = await tryPlay(source);
    /*
     * **待っている間に外された・差し替えられた要素なら何もしない**(#284 の最終レビュー)。
     * 消された要素について「再生を押してください」を出すと、利用者には理由が分からない。
     */
    if (!isStillUsed(this.assignments.values(), source)) return;
    if (outcome === 'unsupported') {
      /*
       * **形式の問題。「再生」を押しても直らない**(#341)。
       *
       * ふつうは受け入れの待ち([`waitForSourceReady`](../../../renderer/src/source.ts))で
       * 弾けている。ここに来るのは**1 枚目を待ちきれずに受け入れた**素材だけ ——
       * **案内だけ変え、セルは外さない**(#341 の同意書の仮定 2)。
       */
      this.showMessage(m.videoUnsupported);
      return;
    }
    if (outcome === 'blocked') {
      /*
       * **自動再生が拒否された。** ミュートでも失敗する環境がある(#33)。
       *
       * **専用のボタンは出さない**(#159)—— 操作 UI の「再生」がその役目を
       * 兼ねる。2 つ出すと「どちらを押せばよいか」が分からない。
       *
       * **`assign` でも出す**(#284 の同意書の仮定 3)—— 割り当て自体は成功している。
       */
      this.showMessage(m.playFailed);
      return;
    }
    /*
     * 自動再生できた。**時間軸も一緒に動かす**(#159)。
     *
     * ここを忘れると**見張りが何もせず、次の「再生」で全部が頭へ戻る。**
     */
    this.playback = autoStartedPlayback(this.playback, Date.now());
    this.updateControls();
  }

  /**
   * 読み込みを待つ。**上限があれば、超えたら「読めなかった」**(URL の素材だけ)。
   *
   * **動画は 1 枚目まで待つ**(#341 —— [`waitForSourceReady`](../../../renderer/src/source.ts))。
   * 寸法だけで受け入れると、Edge では再生できない HEVC がセルに入っていた。
   * 1 枚目の待ちの上限(数秒)は**この上限(URL の 30 秒)の内側**にある。
   */
  private waitForLoad(source: RendererSource, timeoutMs: number | undefined): Promise<boolean> {
    const loaded = waitForSourceReady(source);
    if (timeoutMs === undefined) return loaded;
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        resolve(false);
      }, timeoutMs);
      void loaded.then((ok) => {
        clearTimeout(timer);
        resolve(ok);
      });
    });
  }

  /** 読み込みを待つ要求を数える。 */
  private hold(source: RendererSource): void {
    this.holders.set(source, (this.holders.get(source) ?? 0) + 1);
  }

  /**
   * 待ち終えた。**誰も待たなくなったら「読み込み中」から外す** —— 以後は割り当ての表で共有される
   * (入った場合)か、捨てられる(入らなかった場合)。
   */
  private release(source: RendererSource, key: string): void {
    const left = (this.holders.get(source) ?? 1) - 1;
    if (left > 0) {
      this.holders.set(source, left);
      return;
    }
    this.holders.delete(source);
    if (this.loading.get(key) === source) this.loading.delete(key);
  }

  /** 要素を捨てる。**捨てたことを覚える**(共有元の消失の判定 —— `retired`)。 */
  private retire(source: RendererSource): void {
    this.retired.add(source);
    destroyRendererSource(source);
  }

  /**
   * **使われていなければ**捨てる。割り当ての表で使われている・ほかの要求が読み込みを待っている要素は残す
   * —— 共有している要素を捨てると**他のセルの絵が消え**、待っている要求は「読めなかった」に見える。
   */
  private retireIfUnused(source: RendererSource): void {
    if (this.retired.has(source)) return;
    if (isStillUsed(this.assignments.values(), source)) return;
    if ((this.holders.get(source) ?? 0) > 0) return;
    for (const [key, loading] of this.loading) {
      if (loading === source) this.loading.delete(key);
    }
    this.retire(source);
  }

  /** セルに「最後の要求」の番号を振る。**後から来た要求が勝つ**(ADR-0048 の決定 2)。 */
  private stamp(cells: readonly CellRef[]): number {
    this.requestSeq += 1;
    for (const cell of cells) this.latestRequest.set(cellKey(cell), this.requestSeq);
    return this.requestSeq;
  }

  /** 自分がどのセルでもまだ最後の要求か。**1 つでも負けていれば負け**(半端に入れない)。 */
  private isLatest(cells: readonly CellRef[], ticket: number): boolean {
    return cells.every((cell) => this.latestRequest.get(cellKey(cell)) === ticket);
  }

  // -------------------------------------------------------------------------
  // 素材を割り当てる公開 API(#284 / ADR-0048)
  // -------------------------------------------------------------------------

  /**
   * 呼べる状態か確かめる。**呼べなければ reject の値を投げる。**
   *
   * 読み込みに失敗していればそのコード(`LINK_EXPIRED` など)、WebGL2 が無ければ `UNSUPPORTED`
   * (#283 の C —— 案内は埋め込み先が出す)。
   */
  private assertCallable(): PublicTemplate {
    if (this.destroyed) throw new SignaPreviewError(ASSIGN_ERROR_CODES.NOT_READY);
    if (this.failure !== null) throw new SignaPreviewError(this.failure);
    if (!this.ready || this.template === null || this.renderer === null) {
      throw new SignaPreviewError(ASSIGN_ERROR_CODES.NOT_READY);
    }
    if (this.unsupported) throw new SignaPreviewError(ASSIGN_ERROR_CODES.UNSUPPORTED);
    if (this.exporting !== null) throw new SignaPreviewError(ASSIGN_ERROR_CODES.BUSY);
    return this.template;
  }

  /**
   * **素材をセルに入れる**(`SignaPreview.assign` —— #284)。失敗は `SignaPreviewError` で reject する。
   *
   * **`signa:error` は出さない**(同意書 Q3 —— 呼び出し側が Promise で受ける)。成功したら
   * ドロップと同じく、入ったセルごとに `signa:videoassigned` を出す。
   */
  async assign(source: AssignSource, target?: AssignTarget): Promise<void> {
    const template = this.assertCallable();
    const frames = template.videoFrames;

    const resolved = resolveAssignTarget(frames, target, new Set(this.assignments.keys()));
    if (!resolved.ok) throw new SignaPreviewError(resolved.code);
    const frameKind = frames.find((f) => f.id === resolved.value.frameId)?.sourceKind;

    const prepared = this.prepareForAssign(source, frameKind);
    /*
     * **番号は 1 度だけ振る**(#284 の最終レビュー)。作り直しで振り直すと、
     * その間に来た `clear` / `assign` に負けていたことが消える(後勝ちが破れる)。
     */
    const ticket = this.stamp(linkedCells(frames, resolved.value));
    let placed = await this.place(resolved.value, prepared, ticket);
    /*
     * **共有元が待っている間に捨てられた**ら、1 度だけ作り直す。ドロップは利用者に
     * やり直してもらう(文言を出す)が、プログラムからの呼び出しは自分で取り戻せる。
     */
    if (!placed.ok && placed.reason === 'shared-gone') {
      placed = await this.place(resolved.value, prepared, ticket);
    }
    if (!placed.ok) throw new SignaPreviewError(assignCodeOf(placed.reason, prepared.fromUrl));
    /*
     * **再生の開始は待たない**(#284 の最終レビュー)。割り当てた時点で終える ——
     * URL の素材は再生できるだけ溜まるまで `play()` が終わらず、通信が止まると無期限に待つ。
     * `signa:videoassigned` は `afterPlace` の同期の部分で出るので、resolve より先に届く。
     */
    void this.afterPlace(placed.cells, placed.source);
  }

  /**
   * **セルを空にする**(`SignaPreview.clear` —— #284)。連動先も空になる(ドロップの差し替えと同じ扱い)。
   *
   * **読み込み中の `assign` は負ける**(番号を進める —— ADR-0048 の決定 2)。
   * 誰も使わなくなった要素は破棄する。空いたセルの輪郭と案内は描画コアが戻す(既定の表示のとき)。
   *
   * **同期で終わる**(読み込みが無い)。失敗は投げる —— `embed/index.ts` が Promise に包んで reject にする。
   */
  clear(target?: AssignTarget): void {
    const template = this.assertCallable();
    const frames = template.videoFrames;

    const resolved = resolveClearTargets(frames, target);
    if (!resolved.ok) throw new SignaPreviewError(resolved.code);

    const cells = new Map<string, CellRef>();
    for (const cell of resolved.value) {
      for (const linked of linkedCells(frames, cell)) cells.set(cellKey(linked), linked);
    }
    this.stamp([...cells.values()]);

    const removed: { readonly source: RendererSource }[] = [];
    for (const [key, cell] of cells) {
      const previous = this.assignments.get(key);
      if (previous === undefined) continue;
      removed.push(previous);
      this.assignments.delete(key);
      this.renderer?.setSource(cell.frameId, cell.cellIndex, null);
    }
    // **表を進めきってから 1 回だけ見る**(ADR-0039 決定 4)。共有している要素は捨てない。
    if (removed.length > 0) {
      destroyUnused(removed, this.assignments.values(), (gone) => {
        this.retireIfUnused(gone);
      });
    }
    // **動画が 1 本も無くなったら時間軸を止める** —— 次に入れた動画は頭から始まる。
    if (this.videos().length === 0) this.playback = STOPPED_PLAYBACK;
    // **帯の文言も消す** —— 外したセルについての「再生を押して」などが残ると紛らわしい。
    this.showMessage(null);
    this.updateControls();
  }

  /**
   * `assign` の素材を用意する(種別・共有の鍵・要素の作り方)。
   *
   * **鍵は渡された物の同一性**(`Blob`)か**絶対 URL**(ADR-0048 の決定 3)。
   * 種別は**明示 → 入れる先の枠の `sourceKind` → 動画**(#284 の同意書 Q5)。
   */
  private prepareForAssign(
    source: AssignSource,
    frameKind: SourceKind | null | undefined,
  ): PreparedSource & { readonly fromUrl: boolean } {
    if (source instanceof Blob) {
      // **型から対応しないと分かる Blob(音声・SVG)は読む前に弾く**(#284 の最終レビュー)。
      if (isUnsupportedBlob(source)) {
        throw new SignaPreviewError(ASSIGN_ERROR_CODES.VIDEO_UNSUPPORTED);
      }
      const kind = resolveSourceKind(blobKindOf(source), frameKind);
      return {
        kind,
        key: identityKeyOf(source),
        create: () => createRendererSource(source, kind),
        rejectWhileExporting: true,
        fromUrl: false,
      };
    }
    const url = sourceUrlOf(source.url, document.baseURI);
    const key = urlKeyOf(source.url, document.baseURI);
    if (url === null || key === null) {
      throw new SignaPreviewError(ASSIGN_ERROR_CODES.SOURCE_LOAD_FAILED);
    }
    const kind = resolveSourceKind(source.kind, frameKind);
    return {
      kind,
      key,
      create: () => createRendererSourceFromUrl(url, kind),
      rejectWhileExporting: true,
      loadTimeoutMs: URL_LOAD_TIMEOUT_MS,
      fromUrl: true,
    };
  }

  // -------------------------------------------------------------------------
  // 再生の操作(#159)
  // -------------------------------------------------------------------------

  /** 操作ボタンを 1 つ作る。**クリックは呼び出し側が決める。** */
  private makeControl(label: string, onClick: () => void): HTMLButtonElement {
    const button = el('button', 'signa-button');
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  /**
   * いまの見え方を静止画(PNG)で保存する(#180 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md))。
   *
   * **失敗したら理由を出す。** 黙って壊れない(ADR-0036 決定 8)。
   */
  private async saveImage(): Promise<void> {
    const template = this.template;
    if (template === null) return;

    const m = messages(this.lang);

    // **利用上の注意を出す**(#259)。断られたら何もしない。
    if (!(await this.noticeDialog.ask(m.noticeConfirmSave))) return;

    const url = template.background?.url ?? null;

    let background: HTMLImageElement | null = null;
    if (url !== null) {
      background = await this.loadBackgroundForExport(url);
      /*
       * **背景があるのに読めないなら書き出さない。**
       *
       * 背景抜きの絵は「設置イメージ」として意味を成さない ——
       * 黙って盤面だけの画像を渡すより、**出せないと言うほうがよい。**
       */
      if (background === null) {
        warnExportFailure('背景画像を読み込めませんでした');
        this.showMessage(m.exportFailed);
        return;
      }
    }

    /*
     * **描く前に素材にフレームを持たせる**(#181 で踏んだ)。
     *
     * **一度も再生・シークしていない `<video>` は、`readyState` が 4 でも
     * テクスチャへ転送できないことがある** —— **盤面が黒いまま保存される。**
     * 表示は毎フレーム描き直すので気付かないが、**1 枚だけ描くここは踏む。**
     *
     * `primeVideos` は**元の位置へ戻す**ので、見えている絵は変わらない。
     */
    await primeVideos(this.videos());
    /*
     * **待っている間に捨てられていたら保存しない**(#267)。共有フォルダでは、タブを
     * 切り替えるとシェルを捨てる —— 捨てた後に続けると、**見えていない盤面の画像が
     * ダウンロードされる。** それまでの処理は捨てたシェルの中で閉じていて、外に何も残さない
     * ので、確かめるのは描く直前と、PNG ができた後(`toBlob` のコールバック)の 2 か所でよい。
     */
    if (this.destroyed) return;

    const canvas = composeFrame({
      template: {
        canvas: template.canvas,
        backgroundUrl: url,
        videoFrames: template.videoFrames,
        obstacles: template.obstacles,
      },
      background,
      sourceAt: (frameId, cellIndex) =>
        this.assignments.get(cellKey({ frameId, cellIndex }))?.source ?? null,
    });
    if (canvas === null) {
      warnExportFailure('合成の準備ができませんでした(WebGL2 が使えない、または盤面の寸法が不正)');
      this.showMessage(m.exportFailed);
      return;
    }

    try {
      canvas.toBlob((blob) => {
        /*
         * **PNG を作っている間に捨てられたら保存しない**(#267)。`toBlob` は非同期で、
         * その間にタブを切り替えられる —— 外れた DOM の `<a>` でもダウンロードは始まる。
         */
        if (this.destroyed) return;
        if (blob === null) {
          warnExportFailure('PNG にできませんでした');
          this.showMessage(m.exportFailed);
          return;
        }
        this.download(blob, 'signa-preview.png');
      }, 'image/png');
    } catch (error) {
      // **汚染された canvas は同期的に `SecurityError` を投げる。**
      // 背景を `crossOrigin` 付きで読めていれば起きないが、取りこぼしても落とさない。
      warnExportFailure('PNG にできませんでした', error);
      this.showMessage(m.exportFailed);
    }
  }

  /**
   * いまの見え方を動画(MP4)に書き出す(#181 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md))。
   *
   * **足りていなくても押せる。** 一部のセルが空なら**確認を出して続行させる**(同意書)——
   * 設置イメージの確認では「1 枠だけ入れて見る」が普通にある。
   *
   * **失敗したら理由を出す。** 黙って壊れない(ADR-0036 決定 8)。
   */
  private async runExport(): Promise<void> {
    // **二重に始めさせない。** ボタンは引っ込めているが、連打や外部呼び出しに備える。
    if (this.exporting !== null) return;

    const template = this.template;
    if (template === null) return;

    const m = messages(this.lang);

    /*
     * **利用上の注意 → 足りていないときの確認の順に尋ねる**(#259 / 同意書 Q3)。
     * 先で断られたら後は尋ねない。
     *
     * 足りているかを数えるのは**セル**(#152)——
     * 盤面は分割できるので、枠で数えると「4 分割の 1 セルだけ埋まっている」を
     * 「埋まっている」と誤る。
     */
    const proceed = await askInOrder([
      () => this.noticeDialog.ask(m.noticeConfirmExport),
      () =>
        !needsPartialConfirm(this.assignments.size, totalCells(template.videoFrames)) ||
        globalThis.confirm(m.exportPartial),
    ]);
    /*
     * **尋ねている間の二重起動は起きない。** ダイアログは開いている間の
     * `ask` を断る(notice-dialog.ts)ので、2 本目はここで戻る。
     */
    if (!proceed) return;

    const url = template.background?.url ?? null;
    let background: HTMLImageElement | null = null;
    if (url !== null) {
      background = await this.loadBackgroundForExport(url);
      // **背景があるのに読めないなら書き出さない**(静止画保存と同じ判断)。
      if (background === null) {
        warnExportFailure('背景画像を読み込めませんでした');
        this.showMessage(m.exportFailed);
        return;
      }
    }
    /*
     * **待っている間に捨てられていたら始めない**(#267 —— 静止画の保存と同じ理由)。
     * ここから先は外に残る(離脱の警告・ダウンロード)ので、始める直前に確かめる。
     */
    if (this.destroyed) return;

    const controller = new AbortController();
    this.exporting = controller;
    this.updateControls();
    // **書き出しの間は外の操作(共有フォルダのタブ)を止めてもらう**(#267)。
    this.options.onBusyChange?.(true);

    /*
     * **書き出し中の画面に切り替える**(#233)。
     *
     * - オーバーレイで覆う —— **中止以外の操作を受け付けない**
     * - 表示の描画を止める —— **書き出しにリソースを回す**
     * - 離脱を警告する —— **数十秒の待ちを取りこぼさない**
     *
     * **どの経路でも元に戻す。** 下の `finally` で必ず通る。
     */
    this.beginExportUi();

    try {
      const result = await exportVideo({
        template: {
          canvas: template.canvas,
          backgroundUrl: url,
          videoFrames: template.videoFrames,
          obstacles: template.obstacles,
        },
        background,
        sourceAt: (frameId, cellIndex) =>
          this.assignments.get(cellKey({ frameId, cellIndex }))?.source ?? null,
        videos: this.videos(),
        onProgress: (ratio) => {
          this.showExportProgress(ratio);
        },
        signal: controller.signal,
      });

      if (!result.ok) {
        // **中止は失敗ではない。** 利用者が押した結果なので、何も言わずに戻す。
        if (result.reason === 'aborted') {
          this.showMessage(null);
          return;
        }
        this.showMessage(result.reason === 'unsupported' ? m.exportUnsupported : m.exportFailed);
        return;
      }

      this.download(result.blob, 'signa-preview.mp4');
      // **切ったことは伝える**(ADR-0036 決定 6)。切っていなければ表示を消す。
      this.showMessage(result.truncated ? m.exportTruncated : null);
    } finally {
      /*
       * **成功・中止・失敗・例外のすべてでここを通る**(#233)。
       *
       * **戻し忘れが最も痛い** —— 描画を止めたままだと画面が固まったように見え、
       * `beforeunload` を外し忘れると**書き出しと無関係な離脱でも警告が出る。**
       */
      this.endExportUi();
      this.exporting = null;
      this.updateControls();
      // **成功・中止・失敗のどれでも戻す**(タブを止めたままにしない —— #267)。
      this.options.onBusyChange?.(false);
    }
  }

  // -------------------------------------------------------------------------
  // 書き出し中の画面(#233)
  // -------------------------------------------------------------------------

  /** 書き出し中の画面に切り替える。**[`endExportUi`](#endExportUi) と対にする。** */
  private beginExportUi(): void {
    this.showExportProgress(0);
    this.exportOverlay.hidden = false;
    // **表示の描画を止める。** 素材の再生は続く(書き出しがそれで動くため)。
    this.renderer?.setPaused(true);
    globalThis.addEventListener('beforeunload', this.onBeforeUnload);
  }

  /** 書き出し中の画面を畳む。**必ず呼ぶ**(`finally` から)。 */
  private endExportUi(): void {
    globalThis.removeEventListener('beforeunload', this.onBeforeUnload);
    this.renderer?.setPaused(false);
    this.exportOverlay.hidden = true;
  }

  /** 進捗を反映する(0〜1)。 */
  private showExportProgress(ratio: number): void {
    const percent = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
    this.exportLabel.textContent = messages(this.lang).exporting.replace('{n}', String(percent));
    this.exportFill.style.width = `${String(percent)}%`;
  }

  /**
   * 離脱の警告(#233)。
   *
   * **文言はブラウザが決める** —— 任意の文字列は出せない。
   * **`preventDefault` だけでよい**(`returnValue` は非推奨)。
   * S04 の[離脱警告](../../../web/src/pages/editor/Editor.tsx)と同じ形にそろえてある。
   */
  private readonly onBeforeUnload = (event: BeforeUnloadEvent): void => {
    event.preventDefault();
  };

  /**
   * 書き出し用に背景を読み直す。**`crossOrigin` を付ける。**
   *
   * 付けないと canvas が**汚染され**、`toBlob` が `SecurityError` になる。
   * 一方で**付けると、配信元が CORS ヘッダを返さない場合は読み込み自体が失敗する** ——
   * だから**表示用の `<img>`([`stage.ts`](../../../renderer/src/stage.ts))には付けず、
   * 書き出しのときだけ別に読む。** 表示は今までどおり動く。
   *
   * **同一オリジンなら CORS は要らない。** 本番の V02 単体ページは背景と同じ
   * CloudFront から配信されるので素通りする。ローカルの MinIO は CORS を返す。
   * **V01 埋め込み(第三者サイト)は別オリジンになるので、配信側の設定が要る**(#181 で扱う)。
   */
  private loadBackgroundForExport(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.addEventListener(
        'load',
        () => {
          resolve(image);
        },
        { once: true },
      );
      image.addEventListener(
        'error',
        () => {
          resolve(null);
        },
        { once: true },
      );
      image.src = src;
    });
  }

  /**
   * Blob をダウンロードさせる。
   *
   * **`<a>` をツリーに入れてから押す。** Shadow DOM の中でも効くが、
   * **どこにも入っていない要素の `click()` を無視する実装がある。**
   */
  private download(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    this.root.append(link);
    link.click();
    link.remove();
    // `click()` の時点で取得が始まっているので、ここで解放してよい。
    URL.revokeObjectURL(url);
  }

  /** 再生の対象になる素材(**動画だけ** — #158)。 */
  private videos(): HTMLVideoElement[] {
    return playableOf(this.assignments.values());
  }

  /**
   * まとめて再生する。
   *
   * `fromStart` のときは**時間軸を先頭に戻す**(= 同期)。そうでなければ
   * 止めた位置から続ける。
   *
   * **1 本でも拒否されたら知らせる。** ミュートでも自動再生が拒否される
   * 環境がある(#33 のモバイル対処)。
   */
  private async playAll(fromStart: boolean): Promise<void> {
    const videos = this.videos();
    if (videos.length === 0) return;

    this.playback = startedPlayback(this.playback, Date.now(), fromStart);
    /*
     * **「同期」のときだけ位置を触る**(#172)。
     *
     * 以前は再開時にも時間軸へ合わせ直していたが、**止めた時点でそろって
     * いるので、そのまま続ければそろったまま**である。
     * `currentTime` への代入は**シーク**なので、**触らないほうが速いし正しい。**
     */
    if (fromStart) for (const video of videos) video.currentTime = 0;

    this.updateControls();

    // **`tryPlay` は拒否しない**(理由を値で返す —— #341)。全部を待ってからまとめる。
    const outcomes = await Promise.all(videos.map((video) => tryPlay(video)));

    /*
     * **割り込まれたら何も言わない。** `play()` の約束は `pause()` に
     * 割り込まれると reject する(HTML の仕様)—— 「再生」の直後に「停止」を
     * 押しただけで**偽の失敗**が出てしまう。
     */
    if (this.destroyed || !this.playback.playing) return;

    const m = messages(this.lang);
    // **拒否の理由で案内を分ける**(#341)。形式の問題は「再生」を押しても直らない。
    const outcome = summarizePlayOutcomes(outcomes);
    if (outcome === 'unsupported') {
      this.showMessage(m.videoUnsupported);
    } else if (outcome === 'blocked') {
      this.showMessage(m.playFailed);
    } else if (this.message.textContent === m.playFailed) {
      // **自分が出した失敗だけ消す。** 他の案内(未対応など)は残す。
      this.showMessage(null);
    }
  }

  /** まとめて止める。**位置は保つ**(再生で続きから始まる)。 */
  private pauseAll(): void {
    this.playback = stoppedPlayback(this.playback, Date.now());
    for (const video of this.videos()) video.pause();
    this.updateControls();
  }

  /**
   * 操作ボタンと注意表示を今の割り当てに合わせる。
   *
   * **割り当てが変わるたびに呼ぶ。** 判断は
   * [`playback.ts`](./playback.ts) の純関数に置いてある ——
   * DOM 構築は jsdom で試さないため(ADR-0019)。
   */
  private updateControls(): void {
    const state = controlsState(this.videos().length, this.playback.playing);
    this.controls.hidden = !state.visible;
    this.playButton.disabled = !state.canPlay;
    this.pauseButton.disabled = !state.canPause;
    this.syncButton.disabled = !state.canSync;

    /*
     * **書き出しの出し分け**(#180 / #181 / 同意書 Q1・Q2)。
     *
     * - 素材が 0 —— どちらも出さない
     * - 画像だけ —— 静止画保存
     * - 動画あり —— 動画の書き出し
     *
     * **書き出し中は始める側を引っ込め、中止だけを出す**(二重に始めさせない)。
     */
    /*
     * **リンクが書き出しを許していなければ、動画も静止画も出さない**
     * (#229 / [ADR-0041](../../../../docs/adr/0041-share-link-verification.md) の決定 6)。
     * 判定は `exportControlsState` に閉じてある(jsdom で試せるように — ADR-0019)。
     */
    const exportState = exportControlsState(
      this.videos().length,
      this.assignments.size,
      this.template?.downloadEnabled ?? true,
    );
    const running = this.exporting !== null;
    // **`minimal` では出さない**(#283)。動画の書き出しは今までどおり(下 —— 同意書 Q1)。
    this.saveImageButton.hidden = !this.parts.saveImage || !exportState.canSaveImage || running;
    this.exportButton.hidden =
      this.options.exportEnabled === false || !exportState.canExportVideo || running;
    /*
     * **中止ボタンはオーバーレイの中にある**(#233)。オーバーレイ自体が
     * 書き出し中だけ出るので、**ここで出し分ける必要はない。**
     */

    // **素材を当てたら注意表示を消す**(#159)。すべて解除したら戻す。
    // **`minimal` では出さない**(#283)—— 素材を入れるのは埋め込み先で、利用者ではない。
    this.notice.hidden = !this.parts.noUploadNotice || !showsNoUploadNotice(this.assignments.size);
  }

  // -------------------------------------------------------------------------
  // 状態表示
  // -------------------------------------------------------------------------

  private showLoading(): void {
    this.status.hidden = false;
    this.status.classList.remove('signa-status-error');
    this.status.replaceChildren(document.createTextNode(messages(this.lang).loading));
  }

  private hideLoading(): void {
    this.status.hidden = true;
  }

  private fail(code: ViewerErrorCode): void {
    // **`assign` はこのコードで reject する**(#284)。
    this.failure = code;
    this.emit({ type: 'error', code });

    const text = errorMessage(code, this.lang);
    if (text === null) {
      this.status.hidden = true;
      return;
    }

    this.status.hidden = false;
    this.status.classList.add('signa-status-error');
    this.status.replaceChildren(document.createTextNode(text));

    if (isRetryable(code)) {
      const retry = el('button', 'signa-button');
      retry.textContent = messages(this.lang).retry;
      retry.addEventListener('click', () => {
        void this.start();
      });
      this.status.append(retry);
    }
  }

  private showMessage(text: string | null): void {
    if (text === null) {
      this.message.hidden = true;
      this.message.textContent = '';
      return;
    }
    this.message.hidden = false;
    this.message.textContent = text;
  }

  private emit(event: ShellEvent): void {
    this.options.onEvent?.(event);
  }

  /** 描画コアと動画を解放する。**埋め込み先が要素を外したときに必ず呼ぶ。** */
  destroy(): void {
    this.destroyed = true;
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;

    // **共有していても 1 度だけ捨てる**(#168)。残るものは無い(全部捨てる)。
    destroyUnused(this.assignments.values(), [], (gone) => {
      this.retire(gone);
    });
    // **読み込み中の要素も捨てる。** 待っている要求は `destroyed` を見て終わる。
    for (const source of this.loading.values()) this.retire(source);
    this.loading.clear();
    this.assignments.clear();

    this.renderer?.destroy();
    this.renderer = null;
    this.root.remove();
    this.style.remove();
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}
